
import React, { useReducer, useEffect } from 'react';
import { Lock, ArrowRight, Activity } from 'lucide-react';
import { APP_VERSION } from '../../constants';
import { useData } from '../../hooks/useData';
import { dataManager } from '../../utils/dataManager';

// --- State and Reducer Definition ---

type State = {
  status: 'idle' | 'loading' | 'error' | 'success';
  passwordInput: string;
  shouldShake: boolean;
};

type Action =
  | { type: 'SUBMIT' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR' }
  | { type: 'RESET_SHAKE' }
  | { type: 'UPDATE_PASSWORD'; payload: string };

const initialState: State = {
  status: 'idle',
  passwordInput: '',
  shouldShake: false,
};

const lockScreenReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SUBMIT':
      return { ...state, status: 'loading', shouldShake: false };
    case 'SUCCESS':
      return { ...state, status: 'success' };
    case 'ERROR':
      return { ...state, status: 'error', shouldShake: true, passwordInput: '' };
    case 'RESET_SHAKE':
      return { ...state, shouldShake: false };
    case 'UPDATE_PASSWORD':
      return { ...state, status: 'idle', passwordInput: action.payload };
    default:
      return state;
  }
};

// --- Component Definition ---

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

export const LockScreen: React.FC<LockScreenProps> = ({ onUnlock }) => {
  const [state, dispatch] = useReducer(lockScreenReducer, initialState);
  const { status, passwordInput, shouldShake } = state;
  const customLogo = useData(() => dataManager.getAppLogo());

  // Use custom logo if set, otherwise fallback to permanent default UK Wildcats logo
  const logoSrc = customLogo || '/logo/AppLogo.jpeg';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;

    dispatch({ type: 'SUBMIT' });

    try {
      // Call Cloud Function to verify password
      const isValid = await onUnlock(passwordInput);

      if (isValid) {
        dispatch({ type: 'SUCCESS' });
      } else {
        dispatch({ type: 'ERROR' });
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'ERROR' });
    }
  };

  useEffect(() => {
    if (shouldShake) {
      const timer = setTimeout(() => dispatch({ type: 'RESET_SHAKE' }), 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShake]);

  const isLoading = status === 'loading';

  return (
    <div className="fixed inset-0 bg-[#0B0E14] flex items-center justify-center z-[9999] p-4">
       {/* Background Ambient Effects */}
       <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-900/10 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-900/10 blur-[120px] rounded-full animate-pulse delay-1000" />
       </div>

       <div className="w-full max-w-md relative">
          <div className="text-center mb-12 space-y-4">
             <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500/10 rounded-3xl mb-4 ring-1 ring-blue-500/20 shadow-2xl shadow-blue-500/10 overflow-hidden">
                <img src={logoSrc} alt="App Logo" className="w-full h-full object-cover" />
             </div>
             <h1 className="text-4xl font-black text-white tracking-tight">
                KFPPC <span className="text-blue-500">ACCESS</span>
             </h1>
             <div className="flex items-center justify-center gap-2 text-sm font-medium text-slate-400">
                <Activity className="w-4 h-4 text-emerald-500" />
                <span>System Operational</span>
             </div>
          </div>

          <div className={`bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-3xl p-8 shadow-2xl transition-transform duration-100 ${shouldShake ? 'translate-x-[-10px]' : ''}`}>
             <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                   <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Access Code</label>
                   <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                      <input 
                         type="password" 
                         value={passwordInput}
                         onChange={(e) => dispatch({ type: 'UPDATE_PASSWORD', payload: e.target.value })}
                         className="w-full bg-slate-950/50 border-2 border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 transition-all text-lg font-medium tracking-wide"
                         placeholder="Enter passcode..."
                         autoFocus
                      />
                   </div>
                   {status === 'error' && (
                      <div className="text-red-400 text-xs font-medium pl-1 animate-fadeIn">
                         Access denied. Invalid credentials.
                      </div>
                   )}
                </div>

                <button 
                   type="submit" 
                   disabled={isLoading || !passwordInput}
                   className="w-full bg-white text-black font-black uppercase tracking-widest py-4 rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 shadow-xl flex items-center justify-center gap-2"
                >
                   {isLoading ? "Verifying..." : <>Unlock System <ArrowRight size={18} /></>}
                </button>
             </form>
             
             <div className="mt-12 text-center">
                <div className="text-[10px] text-white/20 font-mono uppercase tracking-wider">Secure Connection v{APP_VERSION}</div>
             </div>
          </div>
       </div>
    </div>
  );
};
